/** Long-running trackers.
 *
 * Deliberately separate from the sprint board. These are counted bodies of work — 73
 * machines, 1,304 phones — driven from a SharePoint spreadsheet, and they do not belong
 * in a two-week box.
 */

import { useState } from 'react'
import { AlertTriangle, ExternalLink, Pencil, RefreshCw } from 'lucide-react'

import { Button, Can, EmptyState, Pill, SectionHeader } from '@/components/ui'
import { Permissions } from '@/lib/session'
import { EditWorkItemDialog } from '@/features/workbench/EditWorkItemDialog'
import { useSyncTracker, useWorkItems, type WorkItem } from '@/features/workbench/useWork'

function staleness(lastSyncedAt: string | null): {
  label: string
  tone: 'green' | 'amber' | 'neutral'
} {
  if (!lastSyncedAt) return { label: 'Never synced', tone: 'neutral' }
  const hours = (Date.now() - new Date(lastSyncedAt).getTime()) / 3_600_000
  // A count that has stopped moving is the failure mode of a silent integration, so
  // age is shown rather than merely stored.
  if (hours < 24) return { label: 'Synced today', tone: 'green' }
  const days = Math.floor(hours / 24)
  return { label: `${days} day${days === 1 ? '' : 's'} ago`, tone: 'amber' }
}

function TrackerRow({ item, onEdit }: { item: WorkItem; onEdit: () => void }) {
  const sync = useSyncTracker()
  const tracker = item.tracker
  const percent = tracker?.percent ?? null
  const age = staleness(tracker?.lastSyncedAt ?? null)

  // The API answers 200 whether or not the read worked, so the button reports what
  // actually happened rather than assuming success.
  const outcome = sync.data
  const failed = outcome && !outcome.succeeded
  // The sheet's own explanation of a zero. After a manual sync the fresh one wins; after a
  // failed one there is nothing to explain — the error says it all.
  const note = outcome ? (outcome.succeeded ? outcome.note : null) : (tracker?.lastNote ?? null)

  return (
    <li className="px-4 py-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="font-medium text-fg">{item.title}</span>
        <div className="flex items-center gap-2">
          {tracker?.lastError && !outcome ? (
            <Pill tone="red">Sync failed</Pill>
          ) : (
            <Pill tone={age.tone}>{age.label}</Pill>
          )}
          <span className="text-sm text-fg-muted">
            {tracker?.done ?? '—'} / {tracker?.total ?? '—'}
          </span>
          <Can permission={Permissions.workEdit}>
            <Button
              onClick={onEdit}
              aria-label={`Edit ${item.title}`}
              title="Edit this tracker — including which sheet, column and done value it reads"
              className="!px-2 !py-1"
            >
              <Pencil size={14} />
            </Button>
            <Button
              onClick={() => sync.mutate(item.id)}
              disabled={sync.isPending}
              aria-label={`Sync ${item.title}`}
              className="!px-2 !py-1"
            >
              <RefreshCw size={14} className={sync.isPending ? 'animate-spin' : undefined} />
              {sync.isPending ? 'Syncing…' : 'Sync now'}
            </Button>
          </Can>
        </div>
      </div>

      <div
        className="mt-2 h-2 w-full overflow-hidden rounded-full"
        role="progressbar"
        aria-valuenow={percent ?? 0}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label={`${item.title} progress`}
        style={{ backgroundColor: 'color-mix(in srgb, var(--fg-muted) 20%, transparent)' }}
      >
        <div
          className="h-full rounded-full transition-all"
          style={{
            width: `${percent ?? 0}%`,
            backgroundColor: percent === 100 ? 'var(--status-green)' : 'var(--brand)',
          }}
        />
      </div>

      <div className="mt-1 flex justify-between text-xs text-fg-muted">
        {/* "Unsynced" rather than 0%: never having read the sheet is not the same as
            having read it and found nothing done. */}
        <span>{percent === null ? 'Unsynced' : `${percent}%`}</span>
        {/* The source sheet, as a link. Every tracker is a number somebody will want to
            check, and "which spreadsheet is this?" is the first question they ask — the
            pilot had this and losing it made the count feel unaccountable. Falls back to
            plain text when the provider has no browsable location. */}
        {tracker?.sourceWebUrl ? (
          <a
            className="inline-flex min-w-0 items-center gap-1 truncate pl-4 underline decoration-dotted hover:text-fg"
            href={tracker.sourceWebUrl}
            target="_blank"
            rel="noreferrer"
            title={`Open ${tracker.sourcePath ?? 'the spreadsheet'}`}
          >
            <ExternalLink size={11} className="shrink-0" aria-hidden />
            <span className="truncate">{tracker.sourceSheet || tracker.sourcePath}</span>
          </a>
        ) : tracker?.sourcePath ? (
          <span className="truncate pl-4">{tracker.sourcePath}</span>
        ) : null}
      </div>

      {failed ? (
        <p className="mt-2 text-xs" style={{ color: 'var(--status-red)' }} role="alert">
          Could not read the spreadsheet: {outcome.error}. The previous count is unchanged.
        </p>
      ) : outcome?.succeeded ? (
        <p className="mt-2 text-xs" style={{ color: 'var(--status-green)' }} role="status">
          Read {outcome.done} of {outcome.total} in {outcome.durationMs}ms.
        </p>
      ) : tracker?.lastError ? (
        <p className="mt-1 text-xs" style={{ color: 'var(--status-red)' }}>
          {tracker.lastError}
        </p>
      ) : null}

      {note ? (
        <p className="mt-1 text-xs" style={{ color: 'var(--status-amber)' }} role="status">
          {note}
        </p>
      ) : null}
    </li>
  )
}

/** `assigneeUserId` narrows the rail to one person's trackers — "My work only". */
export function TrackerRail({ assigneeUserId }: { assigneeUserId?: string } = {}) {
  const { data } = useWorkItems({ trackers: true })
  const allTrackers = data?.items ?? []
  const trackers = assigneeUserId
    ? allTrackers.filter((tracker) => tracker.assigneeUserId === assigneeUserId)
    : allTrackers

  /**
   * A stale count is the failure mode of this whole feature, and it is invisible: the
   * number on screen looks the same whether it was read a minute ago or last month. The
   * hourly sync once threw on every run for seventeen hours after cutover and nothing
   * said so — the per-row age was there to be read, and nobody reads an age unless
   * something points at it.
   *
   * The server decides what counts as stale, so this and the status report agree.
   */
  const stale = trackers.filter((tracker) => tracker.tracker?.isStale)
  const [editing, setEditing] = useState<WorkItem | null>(null)

  return (
    <>
      <SectionHeader>Long-running work</SectionHeader>

      {stale.length > 0 ? (
        <div
          className="card mb-3 p-3"
          role="alert"
          style={{ borderLeft: '3px solid var(--status-red)' }}
        >
          <div className="flex items-center gap-2">
            <AlertTriangle size={15} style={{ color: 'var(--status-red)' }} aria-hidden />
            <span className="text-sm font-semibold text-fg">
              {stale.length === 1
                ? 'One tracker is not refreshing'
                : `${stale.length} trackers are not refreshing`}
            </span>
          </div>
          <p className="mt-1 text-xs text-fg-muted">
            These counts have stopped updating, so treat them as out of date rather than
            current: {stale.map((tracker) => tracker.title).join(', ')}. Try
            &ldquo;Sync now&rdquo; — if it fails, the error explains why.
          </p>
        </div>
      ) : null}

      {trackers.length === 0 && assigneeUserId && allTrackers.length > 0 ? (
        <EmptyState title="None of the long-running work is assigned to you">
          Turn off &ldquo;My work only&rdquo; to see everyone&apos;s trackers.
        </EmptyState>
      ) : trackers.length === 0 ? (
        <EmptyState title="No trackers yet">
          A tracker follows a count in a SharePoint spreadsheet — machines replaced, phones
          migrated — instead of being finished in a sprint. Counts refresh hourly, and
          &ldquo;Sync now&rdquo; reads the sheet immediately.
        </EmptyState>
      ) : (
        <ul className="card divide-y divide-border">
          {trackers.map((tracker) => (
            <TrackerRow key={tracker.id} item={tracker} onEdit={() => setEditing(tracker)} />
          ))}
        </ul>
      )}

      {editing ? <EditWorkItemDialog item={editing} onClose={() => setEditing(null)} /> : null}
    </>
  )
}
